import { Flex } from "grid-styled";
import { Fragment, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import system from "system-components";

import ImportFromWordModal from "./ImportFromWordModal";

import Editor from "pstat-anywhere/components/editor/editor";
import {
  CSRF_TOKEN_NAME,
  getCSRFToken
} from "pstat-anywhere/utils/customFetch";
import {
  POLICY_ACTIONS,
  triggerPolicyEvent
} from "pstat-anywhere/utils/googleTagManager";
import { getUrl } from "pstat-anywhere/utils/urls";
import Panel from "pstat-design-system/Panel";
import { H2, Text } from "pstat-design-system/text";


const ImportText = system({
  is: Text,
  color: "secondary.0",
  ml: 4
}).extend`
  cursor: pointer;
`;

const ImportFromWord = props => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const openModal = () => setOpen(true);
  const closeModal = () => setOpen(false);
  const handleKeyDown = event => {
    if (event.key === "Enter") {
      openModal();
    }
  };
  return (
    <Fragment>
      <ImportText tabIndex="0" onClick={openModal} onKeyDown={handleKeyDown}>
        {t("documentControl.create.importFromWord.import")}
      </ImportText>
      <ImportFromWordModal
        isOpen={open}
        onClose={closeModal}
        html={props.html}
        onHtmlChange={props.onHtmlChange}
        documentSave={props.documentSave}
        setAttachments={props.setAttachments}
        document={props.document}
        isOverridePage={props.isOverridePage}
      />
    </Fragment>
  );
};

const Export = ({ name, html, temporarilyDisableBeforeUnload, document }) => {
  const { t } = useTranslation();
  const form = useRef(null);
  const exportToWord = () => {
    temporarilyDisableBeforeUnload();
    triggerPolicyEvent(
      POLICY_ACTIONS.DOWNLOAD_TO_WORD,
      document?.pk,
      name,
      document?.category?.pk,
      document?.category?.name
    );
    form.current.submit();
  };
  const handleKeyDown = event => {
    if (event.key === "Enter") {
      exportToWord();
    }
  };
  return (
    <Fragment>
      <form method="POST" action={getUrl("/msword/download/")} ref={form}>
        <input type="hidden" name="name" value={name} />
        <input type="hidden" name="html" value={html} />
        <input type="hidden" name={CSRF_TOKEN_NAME} value={getCSRFToken()} />
      </form>
      <ImportText onClick={exportToWord} onKeyDown={handleKeyDown} tabIndex="0">
        {t("documentControl.create.exportFromWord.export")}
      </ImportText>
    </Fragment>
  );
};

const Header = ({
  name,
  policyName,
  policyHtml,
  onHtmlChange,
  documentSave,
  setAttachments,
  temporarilyDisableBeforeUnload,
  enableEditWithWord,
  document,
  isOverridePage
}) => (
  <Flex justifyContent="space-between">
    <H2 color="nav.0" fontWeight="normal">
      {name}
    </H2>
    {enableEditWithWord && (
      <div>
        <Export
          name={policyName}
          html={policyHtml}
          temporarilyDisableBeforeUnload={temporarilyDisableBeforeUnload}
          document={document}
        />
        <ImportFromWord
          html={policyHtml}
          onHtmlChange={onHtmlChange}
          documentSave={documentSave}
          setAttachments={setAttachments}
          document={document}
          isOverridePage={isOverridePage}
        />
      </div>
    )}
  </Flex>
);

const EditorSection = ({
  policyForm,
  onChange,
  documentSave,
  rtlSupport,
  newCkeditor,
  ckeditor5,
  setAttachments,
  temporarilyDisableBeforeUnload,
  enableEditWithWord,
  setEditor,
  document,
  isOverridePage,
  editorStyles
}) => {
  const { t } = useTranslation();
  return (
    <Panel
      id="editor"
      name={t("documentControl.edit.editorSection")}
      hideHeaderBorder={true}
      renderHeader={Header}
      policyName={policyForm.name}
      policyHtml={policyForm.html}
      onHtmlChange={onChange}
      documentSave={documentSave}
      setAttachments={setAttachments}
      temporarilyDisableBeforeUnload={temporarilyDisableBeforeUnload}
      enableEditWithWord={enableEditWithWord}
      document={document}
      isOverridePage={isOverridePage}
    >
      <Editor
        content={policyForm.html}
        onChange={onChange}
        rtlSupport={rtlSupport}
        newCkeditor={newCkeditor}
        ckeditor5={ckeditor5}
        setEditor={setEditor}
        editorStyles={editorStyles}
      />
    </Panel>
  );
};

export default EditorSection;
